/**
 * Upload formats for the sidebar images.
 *
 * SQUAD's layout document of 11.09.2026 fixes two shapes: 16:9 for the Fondsberater
 * photo and 21:9 for the logo blocks. Alex settled on 21:9 over 2:1 because it is
 * flatter and wastes less of the column ("find ich dann besser als 2:1, da nicht so
 * hoch") — and it is a standard shape, which was his argument for 16:9 as well.
 *
 * Both are decided in the call: the whitespace around the subject is baked into the
 * file rather than added by the layout, so the customer controls how close the image
 * sits to the text without us shipping a new build. The frame here is therefore the
 * whole image, and it is drawn edge to edge.
 *
 * The pixel sizes are the ones to hand SQUAD. The sidebar column is 166 pt wide, so
 * 300 dpi would need about 692 px; these are comfortably above that and are the
 * familiar round numbers for their ratio, which matters more than the exact figure —
 * anything larger is wasted on a 166 pt column, and the 5 MB upload limit is the real
 * ceiling.
 */

export interface SquadImageFormat {
  /** How the ratio is written when talking to the customer. */
  ratio: string;
  widthPx: number;
  heightPx: number;
  /** width / height — CSS aspect-ratio, and the PDF frame. */
  aspect: number;
}

const format = (ratio: string, widthPx: number, heightPx: number): SquadImageFormat => ({
  ratio,
  widthPx,
  heightPx,
  aspect: widthPx / heightPx,
});

export const SQUAD_IMAGE_FORMATS = {
  /** Fondsberater photo, page 1, above the description. */
  advisorPhoto: format('16:9', 1280, 720),
  /**
   * Logo blocks: under the description on page 1, and optional at the foot of page 2.
   * 1280 wide like the photo (agreed 25.09.2026), so every image SQUAD supplies has one
   * width — which makes the frame 64:27, a hair flatter than a true 21:9 (1260 × 540).
   */
  partnerLogo: format('ca. 21:9', 1280, 540),
} as const;

/** Helper line under an upload field. */
export const squadImageFormatHint = (f: SquadImageFormat): string =>
  `${f.ratio} — ${f.widthPx} × ${f.heightPx} px. Abstände bitte im Bild vorsehen.`;
